"use client"

import React, {useEffect, useLayoutEffect, useRef} from "react";
import {motion, useAnimationControls, useReducedMotion} from "framer-motion";
import {cn} from "@/lib/utils";
import Image from "next/image";
import gsap from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

import dashedLine from "@/public/leaderboards/leaderboards-dashed-line.svg";
import rank1 from "@/public/animatedAssets/leaderboards-rank1.jpg";
import rank2 from "@/public/animatedAssets/leaderboards-rank2.jpg";
import rank3 from "@/public/animatedAssets/leaderboards-rank3.jpg";
import rank4 from "@/public/animatedAssets/leaderboards-rank4.jpg";
import rank5 from "@/public/animatedAssets/leaderboards-rank5.jpg";

const leaderboardData = [
  { name: "Sophia Hernandez", image: rank1 },
  { name: "Jim Took", image: rank2 },
  { name: "Julian Budach", image: rank3, willBeRank1: true },
  { name: "Henry Jah", image: rank4 },
  { name: "Reginald Schuster", image: rank5 },
];

type State = "collapsed" | "expanded";

export const AnimatedAsset8 = ({
  className = "",
}: {
  className?: string;
}) => {
  const controls = useAnimationControls();
  const prefersReduced = useReducedMotion();
  const stateRef = useRef<State>("collapsed");
  const rootRef = useRef<HTMLDivElement | null>(null);

  const setState = (next: State) => {
    if (stateRef.current === next) return Promise.resolve();
    stateRef.current = next;
    return controls.start(next);
  };

  useEffect(() => {
    controls.set("collapsed");
    stateRef.current = "collapsed";
  }, [controls]);

  useLayoutEffect(() => {
    if (prefersReduced) return;
    const el = rootRef.current;
    if (!el) return;

    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

    if (isTouchDevice) {
      const st = ScrollTrigger.create({
        trigger: el,
        start: "top 50%",
        end: "bottom 50%",
        onEnter: () => setState("expanded"),
        onLeave: () => {},
        onEnterBack: () => setState("expanded"),
        onLeaveBack: () => setState("collapsed"),
        invalidateOnRefresh: true,
      });

      return () => st.kill();
    }

    return;
  }, [prefersReduced, controls]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseEnter = async () => {
    if (prefersReduced) return;
    await setState("expanded");
  };

  const handleMouseLeave = () => {
    if (prefersReduced) return;
    setState("collapsed");
  };

  const ease = {ease: "circOut", duration: .6};
  const rankEase = {ease: "circOut", duration: .3};

  const dashedLines = {
    collapsed: {y: "-50%", transition: ease},
    expanded: {y: "calc(-50% - 4rem)", transition: ease},
  }

  const gradientTop = {
    collapsed: {y: 0, transition: ease},
    expanded: {y: "-100%", transition: ease},
  }

  const gradientBottom = {
    collapsed: {y: 0, transition: ease},
    expanded: {y: "50%", transition: ease},
  }

  const initialRank = {
    collapsed: { opacity: 1, transition: rankEase },
    expanded: { opacity: 0, transition: rankEase },
  };

  const alternativeRank = {
    collapsed: { opacity: 0, transition: rankEase },
    expanded: { opacity: 1, transition: rankEase },
  };

  const getItemVariants = (index: number) => {
    const rank1Index = leaderboardData.findIndex(item => item.willBeRank1);

    if (rank1Index === -1 || rank1Index === index) {
      if (index === rank1Index) {
        return {
          collapsed: { y: 0, transition: ease },
          expanded: { y: `${-index * 9}rem`, transition: ease }, // 9 rem = 6 rem height + 3rem gap
        };
      }
      return null;
    }

    if (index < rank1Index) {
      return {
        collapsed: { y: 0, transition: ease },
        expanded: { y: "9rem", transition: ease },
      };
    }

    return null;
  }

  return (
    <div
      ref={rootRef}
      className={cn(
        "w-full h-[50rem] bg-[#0A1A1D] flex flex-col items-start justify-start px-[2rem] overflow-clip pt-[2.5rem]",
        "relative after:absolute after:h-[2px] after:w-full after:left-0 after:bottom-0 after:bg-[#D9F9B9]",
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        className="w-[2.125rem] absolute left-[9rem] top-1/2 -translate-y-1/2"
        variants={dashedLines}
        animate={controls}
        initial="collapsed"
      >
        <Image
          className="!w-full"
          src={dashedLine}
          alt="dashed Line"
        />
      </motion.div>

      <motion.div
        className="h-[25rem] opacity-70 w-full absolute left-0 top-0 bg-gradient-to-b from-[#0A1A1D] pointer-events-none"
        variants={gradientTop}
        animate={controls}
        initial="collapsed"
      />

      <div className="flex flex-col gap-[3rem] pl-[2rem] relative">
        {leaderboardData.map((item, index) => {
          const variants = getItemVariants(index);
          const ItemWrapper = variants ? motion.div : 'div';
          const animationProps = variants ? {
            variants,
            animate: controls,
            initial: "collapsed"
          } : {};

          return (
            <ItemWrapper
              key={index}
              className="flex items-center relative gap-[9rem] h-[6rem]"
              {...animationProps}
            >
              <div className="w-[3rem] h-[3rem] flex items-center justify-center flex-shrink-0 relative">
                {variants ? (
                  <>
                    <motion.p
                      variants={initialRank}
                      animate={controls}
                      initial="collapsed"
                      className={cn(
                        "mb-0 leading-none font-geistmono text-[2.25rem] absolute",
                        index === 0 ? "text-[#D9F9B9]/80" : "text-white/[.15]"
                      )}
                    >
                      {index + 1}
                    </motion.p>
                    {item.willBeRank1 && (
                      <motion.p
                        variants={alternativeRank}
                        animate={controls}
                        initial="collapsed"
                        className="absolute mb-0 leading-none font-geistmono text-[2.25rem] text-[#D9F9B9]/80"
                      >
                        1
                      </motion.p>
                    )}
                    {!item.willBeRank1 && index < leaderboardData.findIndex(item => item.willBeRank1) && (
                      <motion.p
                        variants={alternativeRank}
                        animate={controls}
                        initial="collapsed"
                        className="absolute mb-0 leading-none font-geistmono text-[2.25rem] text-white/[.15]"
                      >
                        {index + 2}
                      </motion.p>
                    )}
                  </>
                ) : (
                  <p className="mb-0 leading-none font-geistmono text-[2.25rem] text-white/[.15] opacity-80">
                    {index + 1}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-[2rem] relative">
                {item.willBeRank1 && (
                  <div className="absolute w-[calc(100%+3rem)] h-[calc(100%+1.5rem)] -left-[.75rem] top-1/2 -translate-y-1/2 bg-white rounded-[1rem]">
                    <div className={cn(
                      "absolute h-[3.8rem] w-[1.9rem] top-1/2 -translate-y-1/2 left-px -translate-x-full bg-white",
                      "[clip-path:polygon(0_1.9rem,1.9rem_0,100%_0,100%_100%,1.9rem_100%,0_calc(100%-1.9rem))]"
                    )}/>
                  </div>
                )}

                <Image
                  className="!w-[6rem] rounded-full flex-shrink-0 relative"
                  src={item.image}
                  alt=""
                />
                <p className={cn(
                  "mb-0 leading-none flex-shrink-0 text-white/50 text-[2.25rem] relative",
                  item.willBeRank1 ? "text-black" : "text-white/50 font-light"
                )}>
                  {item.name}
                </p>
              </div>
            </ItemWrapper>
          );
        })}
      </div>

      <motion.div
        className="h-[21.125rem] w-full absolute left-0 bottom-0 bg-gradient-to-t from-[#0A1A1D] pointer-events-none"
        variants={gradientBottom}
        animate={controls}
        initial="collapsed"
      />
    </div>
  );
};
